// src/components/LinkifiedCardText.jsx
"use client";

import { useEffect, useMemo, useState } from "react";
import CardText from "@/components/card/CardText";
import { db } from "@/firebase/config";
import { doc, getDoc } from "@/lib/firestore-instrumented";

// handleLower -> boolean (is agent)
const AGENT_HANDLE_CACHE = new Map();

const MENTION_RE = /(^|[^`/\\w])@([a-z0-9-]{3,32})\b/gi;
const HUB_PLUS_RE = /(^|[^`/\\w])\+([a-z0-9-]{3,64})\b/gi;
// Note: # is reserved for future tag support. Do not linkify for now.

function normalizeHandle(handle = "") {
  return handle.trim().toLowerCase();
}

function extractMentions(text = "") {
  const handles = new Set();
  let match;
  while ((match = MENTION_RE.exec(text)) !== null) {
    const handle = normalizeHandle(match[2]);
    if (handle) handles.add(handle);
  }
  MENTION_RE.lastIndex = 0;
  return Array.from(handles);
}

function linkifyContent(content = "", agentMap = {}) {
  const withMentions = content.replace(MENTION_RE, (match, prefix, handle) => {
    const normalized = normalizeHandle(handle);
    const isAgent = agentMap[normalized] === true;
    const linkTarget = isAgent ? `/agent/${encodeURIComponent(normalized)}` : `/u/${handle}`;
    return `${prefix}[${`@${handle}`}](${linkTarget})`;
  });

  return withMentions.replace(HUB_PLUS_RE, (match, prefix, slug) => {
    const linkTarget = `/h/${slug}`;
    return `${prefix}[${`+${slug}`}](${linkTarget})`;
  });
}

export default function LinkifiedCardText({ content = "", ...rest }) {
  const mentionHandles = useMemo(() => extractMentions(content), [content]);
  const [agentMap, setAgentMap] = useState(() => {
    const initial = {};
    mentionHandles.forEach((handle) => {
      if (AGENT_HANDLE_CACHE.has(handle)) {
        initial[handle] = AGENT_HANDLE_CACHE.get(handle) || false;
      }
    });
    return initial;
  });

  useEffect(() => {
    let active = true;
    const unknownHandles = mentionHandles.filter((handle) => !AGENT_HANDLE_CACHE.has(handle));

    const applyCacheToState = () => {
      if (!active) return;
      setAgentMap((prev) => {
        const next = {};
        mentionHandles.forEach((handle) => {
          if (AGENT_HANDLE_CACHE.has(handle)) {
            next[handle] = AGENT_HANDLE_CACHE.get(handle) || false;
          } else if (prev.hasOwnProperty(handle)) {
            next[handle] = prev[handle];
          }
        });
        return next;
      });
    };

    if (!unknownHandles.length) {
      applyCacheToState();
      return () => {
        active = false;
      };
    }

    (async () => {
      for (const handle of unknownHandles) {
        try {
          const normalized = normalizeHandle(handle);
          const snap = await getDoc(doc(db, "agentHandles", normalized));
          if (snap.exists()) {
            AGENT_HANDLE_CACHE.set(normalized, true);
          } else {
            AGENT_HANDLE_CACHE.set(normalized, false);
          }
        } catch (err) {
          console.warn("[LinkifiedCardText] failed to resolve agent handle:", handle, err?.message || err);
          AGENT_HANDLE_CACHE.set(normalizeHandle(handle), false);
        }
      }
      applyCacheToState();
    })();

    return () => {
      active = false;
    };
  }, [mentionHandles]);

  const transformed = useMemo(() => linkifyContent(content, agentMap), [content, agentMap]);

  return <CardText content={transformed} {...rest} />;
}
